import React, { useState, useEffect } from 'react';
import './Home.css';
import Hero from './Hero';
import Categories from './Categories';
import FeaturedProducts from './FeaturdProducts';
import Header from '../../../components/layout/Header/Header';
import commentsService from '../../../services/comments.service';
import productsService from '../../../services/products.service';
import Footer from '../../../components/layout/Footer/Footer';
import ParallaxFeaturesSection from './ParallaxFeaturesSection';


const Home = () => {
  const [comments, setComments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    author: '',
    email: '',
    productId: '',
    productName: '',
    rating: 5,
    comment: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    fetchApprovedComments();
    fetchProducts();
  }, []);

  const fetchApprovedComments = async () => {
    try {
      const result = await commentsService.getApprovedComments(6);
      if (result.success) {
        setComments(result.comments);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const result = await productsService.getAllProducts();
      if (result.success) {
        setProducts(result.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductSelect = (e) => {
    const productId = e.target.value;
    const selectedProduct = products.find(p => p.id === productId);
    setFormData(prev => ({
      ...prev,
      productId: productId,
      productName: selectedProduct ? selectedProduct.name : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);

    try {
      const result = await commentsService.addComment(formData);
      console.log('Add comment result:', result);
      if (result.success) {
        setSubmitStatus({ type: 'success', message: 'Votre commentaire a été soumis avec succès! Il sera visible après approbation.' });
        setFormData({
          author: '',
          email: '',
          productId: '',
          productName: '',
          rating: 5,
          comment: ''
        });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus({ type: 'error', message: 'Erreur lors de la soumission. Veuillez réessayer.' });
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      setSubmitStatus({ type: 'error', message: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-DZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="home">
      <Header />
      {/* Desktop: Categories then Hero */}
      <div className="home-desktop-layout">
        <Categories />
        <Hero />
      </div>

      {/* Mobile: Hero then Categories */}
      <div className="home-mobile-layout">
        <Hero />
        <Categories />
      </div>
      <FeaturedProducts />

      {/* Enhanced Features Section */}
      <ParallaxFeaturesSection />

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="Home-section-title">Ce Que Disent Nos Clients</h2>
            <p className="section-subtitle">Des avis authentiques de clients satisfaits</p>
          </div>

          {comments.length > 0 ? (
            <div className="testimonials__grid">
              {comments.map((comment) => (
                <div key={comment.id} className="testimonial-card">
                  <div className="testimonial-header">
                    <div className="testimonial-avatar">
                      {comment.avatar || comment.author.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="testimonial-info">
                      <div className="testimonial-name">{comment.author}</div>
                      <div className="testimonial-date">{formatDate(comment.createdAt)}</div>
                    </div>
                  </div>

                  <div className="testimonial-rating">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`star ${i < comment.rating ? 'star--filled' : ''}`}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>

                  <p className="testimonial-text">{comment.comment}</p>

                  <div className="testimonial-product">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                    {comment.productName}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="testimonials-empty">
              <p>Aucun avis disponible pour le moment</p>
            </div>
          )}
        </div>
      </section>

      {/* Comment Form Section */}
      <section className="comment-form-section">
        <div className="container">
          <div className="comment-form-container">
            <div className="comment-form-header">
              <h2 className="home-section-title">Partagez Votre Expérience</h2>
              <p className="section-subtitle">Votre avis compte pour nous et aide les autres clients</p>
            </div>

            {submitStatus && (
              <div className={`submit-status submit-status--${submitStatus.type}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {submitStatus.type === 'success' ? (
                    <polyline points="20 6 9 17 4 12" />
                  ) : (
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </>
                  )}
                </svg>
                <p>{submitStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="comment-form">
              <div className="form-row">
                <div className="form-group " >
                  <label className='home-form-group-label' htmlFor="author">Votre Nom *</label>
                  <input
                    type="text"
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Ahmed Benali"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Votre Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="votre@email.dz"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="productId">Produit Concerné *</label>
                <select
                  id="productId"
                  name="productId"
                  value={formData.productId}
                  onChange={handleProductSelect}
                  required
                >
                  <option value="">Sélectionnez un produit</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="rating">Votre Note *</label>
                <div className="rating-input">
                  {[5, 4, 3, 2, 1].map((value) => (
                    <React.Fragment key={value}>
                      <input
                        type="radio"
                        id={`star${value}`}
                        name="rating"
                        value={value}
                        checked={formData.rating === value}
                        onChange={() => setFormData(prev => ({ ...prev, rating: value }))}
                      />
                      <label htmlFor={`star${value}`}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="comment">Votre Commentaire *</label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  placeholder="Partagez votre expérience avec ce produit..."
                />
              </div>

              <button type="submit" className="btn btn--submit" disabled={loading}>
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    Envoyer Mon Avis
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Enhanced Newsletter Section */}
      <section className="newsletter">
        <div className="container">
          <div className="newsletter__content">
            <h2 className="newsletter__title">Restez Informé</h2>
            <p className="newsletter__description">
              Inscrivez-vous à notre newsletter pour recevoir nos offres exclusives,
              nouvelles collections et conseils d'experts en aménagement de bureau
            </p>
            <form className="newsletter__form">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="newsletter__input"
                required
              />
              <button type="submit" className="btn btn--primary">
                S'inscrire
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Home;